import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import CourseHeader from "../components/Courses/Shared/CourseHeader";
import ciscoImg from "../assets/cisco.png";
import cmsApi from "../services/cmsApi";
import { getImageUrl } from "../utils/getImageUrl";
import { getCareerPaths, findCareerPath, getCoursesForLevel, LANGUAGE_LABELS, capitalize } from "../utils/ciscoCareerPaths";

// Rendered when a URL segment (e.g. "beginner") matches a `level` grouping
// key shared by multiple language-specific courses, rather than a single
// course's own slug — e.g.
// /courses/professional-courses/cisco-networking-academy/programming/beginner
// lists Python/JavaScript/C++ Essentials 1 with a language filter, instead of
// going straight to one course's own detail page (see CiscoCourseDetailPage).
const CiscoLevelCoursesPage = ({ pathSlug, levelSlug }) => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState("all");

  useEffect(() => {
    window.scrollTo(0, 0);
    cmsApi
      .getProgramBySlug("cisco-networking-academy")
      .then((res) => {
        if (res.success && res.program) setProgram(res.program);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [pathSlug, levelSlug]);

  const paths = getCareerPaths(program?.ciscoCertTracksSection ?? null);
  const path = findCareerPath(paths, pathSlug);
  const levelCourses = getCoursesForLevel(path, levelSlug);

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex justify-center items-center py-32">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-custom-pink" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!path || levelCourses.length === 0) {
    return (
      <div>
        <Navbar />
        <div className="mid-desktop-page container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl py-24 text-center">
          <h1 className="text-3xl font-bold text-custom-pink mb-4">Course not found</h1>
          <p className="text-gray-600 mb-8">
            We couldn&apos;t find that course. It may have been renamed or removed.
          </p>
          <Link
            to="/courses/professional-courses/cisco-networking-academy"
            className="inline-block bg-custom-pink text-white font-bold px-6 py-3 rounded-lg hover:opacity-90 transition-opacity"
          >
            &larr; Back to Discover Our Certifications
          </Link>
        </div>
        <Footer />
        <FloatingSocialPanels />
      </div>
    );
  }

  const availableLanguages = [...new Set(levelCourses.map((c) => c.language).filter(Boolean))];
  const filteredCourses =
    selectedLanguage === "all" ? levelCourses : levelCourses.filter((c) => c.language === selectedLanguage);
  const levelLabel = capitalize(levelSlug);

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">
        <CourseHeader
          title={path.name}
          highlight={levelLabel}
          subtitle={`Choose a language to get started with ${path.name} — ${levelLabel}.`}
          backgroundImage={
            (() => {
              const src = path.levelHeroImages?.[levelSlug] || path.heroImage || program?.heroImage;
              return src ? getImageUrl(src) : ciscoImg;
            })()
          }
          cta={{ label: "Get Started", targetId: "level-courses" }}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Professional Courses", href: "/course/professional-courses" },
            { label: "Cisco Networking Academy", href: "/courses/professional-courses/cisco-networking-academy" },
            { label: `${path.name} Career Path`, href: `/courses/professional-courses/cisco-networking-academy/${path.slug}` },
            { label: levelLabel },
          ]}
        />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl py-16 md:py-24">
          <Link
            to={`/courses/professional-courses/cisco-networking-academy/${path.slug}`}
            className="mb-8 inline-flex items-center gap-2 text-custom-pink font-semibold hover:underline"
          >
            <span aria-hidden="true">&larr;</span> Back to {path.name} Career Path
          </Link>

          <h2 id="level-courses" className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 scroll-mt-28">
            {levelLabel} Courses
          </h2>

          {availableLanguages.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 mb-10">
              <button
                type="button"
                onClick={() => setSelectedLanguage("all")}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                  selectedLanguage === "all"
                    ? "bg-custom-pink text-white border-custom-pink"
                    : "bg-white text-gray-700 border-gray-300 hover:border-custom-pink/50"
                }`}
              >
                All
              </button>
              {availableLanguages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setSelectedLanguage(lang)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    selectedLanguage === lang
                      ? "bg-custom-pink text-white border-custom-pink"
                      : "bg-white text-gray-700 border-gray-300 hover:border-custom-pink/50"
                  }`}
                >
                  {LANGUAGE_LABELS[lang] || lang}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <Link
                key={course.slug}
                to={`/courses/professional-courses/cisco-networking-academy/${path.slug}/${course.slug}`}
                className="flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md hover:border-custom-pink/30 transition-all duration-300"
              >
                {course.image ? (
                  <img
                    src={getImageUrl(course.image)}
                    alt={course.name}
                    className="w-full h-36 object-cover flex-shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-full h-36 bg-gradient-to-br from-custom-pink/10 to-gold/10 flex-shrink-0" />
                )}
                <div className="p-5 flex-1 flex flex-col">
                  {course.language && (
                    <span className="text-xs font-semibold text-custom-pink uppercase tracking-wide mb-1">
                      {LANGUAGE_LABELS[course.language] || course.language}
                    </span>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{course.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{course.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 font-medium">
                    {course.hours && <span>{course.hours}</span>}
                    {course.fee && <span>{course.fee}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default CiscoLevelCoursesPage;
